import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { eventoService, unidadeService } from '../../services/api';
import { handleApiError } from '../../utils/apiError';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import SessoesPainel from './SessoesPainel';

const { TextArea } = Input;

// Fase A do módulo de Eventos: só ATIVO/CANCELADO (sem RASCUNHO/PUBLICADO/
// ENCERRADO -- nenhum teria comportamento real nesta fase, ver
// backend/app/models/evento.py).
const STATUS_COLORS = { ATIVO: 'green', CANCELADO: 'red' };

// Filtro operacional de status (Fase Eventos-Operacional 1). O default da
// tela e' 'ATIVO' (cancelados ocultos por padrao); 'TODOS' NAO envia o
// parametro `status` (o backend, sem status, retorna ATIVO+CANCELADO --
// semantica preservada, sem quebrar outros consumidores).
const FILTRO_STATUS_PADRAO = 'ATIVO';
const OPCOES_FILTRO_STATUS = [
  { value: 'ATIVO', label: 'Ativos' },
  { value: 'CANCELADO', label: 'Cancelados' },
  { value: 'TODOS', label: 'Todos' },
];

const EventosAdmin = () => {
  const {
    loading: permissaoLoading,
    isAdmin,
    podeVisualizar,
    podeCriar,
    podeEditar,
    podeExcluir,
  } = usePermissaoRecurso('eventos');

  const [eventos, setEventos] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [filtroStatus, setFiltroStatus] = useState(FILTRO_STATUS_PADRAO);
  const [filtroUnidade, setFiltroUnidade] = useState(undefined);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [form] = Form.useForm();

  const carregarEventos = useCallback(async (paginaAtual, tamanhoPagina, filtros = {}, sort = {}) => {
    setLoading(true);
    try {
      const params = { page: paginaAtual, page_size: tamanhoPagina };
      // 'TODOS' => nao envia status (backend retorna ATIVO+CANCELADO);
      // 'ATIVO'/'CANCELADO' => filtro exato.
      if (filtros.status && filtros.status !== 'TODOS') {
        params.status = filtros.status;
      }
      if (filtros.id_unidades) {
        params.id_unidades = filtros.id_unidades;
      }
      // Ordenacao SERVER-SIDE: so envia quando ambos definidos.
      if (sort.sortBy && sort.sortOrder) {
        params.sort_by = sort.sortBy;
        params.sort_order = sort.sortOrder;
      }
      const resp = await eventoService.list(params);
      setEventos(resp.data.items || []);
      setTotal(resp.data.total || 0);
      setPage(resp.data.page || paginaAtual);
      setPageSize(resp.data.page_size || tamanhoPagina);
    } catch (err) {
      message.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  }, []);

  // Recarrega mantendo os filtros/ordenacao atuais (usado apos criar/editar/
  // cancelar/excluir). Le o estado atual -- nao memoizado de proposito.
  const recarregar = (paginaAtual = page, tamanhoPagina = pageSize) =>
    carregarEventos(
      paginaAtual,
      tamanhoPagina,
      { status: filtroStatus, id_unidades: filtroUnidade },
      { sortBy, sortOrder },
    );

  const carregarUnidades = useCallback(async () => {
    try {
      const resp = await unidadeService.list();
      setUnidades(resp.data || []);
    } catch (err) {
      // Silencioso: falha ao carregar unidades não deve bloquear a tela,
      // só limita as opções do select de criação/edição.
    }
  }, []);

  useEffect(() => {
    if (!permissaoLoading && (podeVisualizar || isAdmin)) {
      // Abertura da tela: universo operacional padrao (somente ATIVOS).
      carregarEventos(1, pageSize, { status: FILTRO_STATUS_PADRAO }, {});
      carregarUnidades();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissaoLoading, podeVisualizar, isAdmin]);

  const abrirCriar = () => {
    setEditingId(null);
    form.resetFields();
    setModalAberto(true);
  };

  const abrirEditar = (evento) => {
    setEditingId(evento.id);
    form.setFieldsValue({
      id_unidades: evento.id_unidades,
      nome: evento.nome,
      descricao: evento.descricao,
    });
    setModalAberto(true);
  };

  const handleSubmit = async (values) => {
    setSalvando(true);
    try {
      if (editingId) {
        await eventoService.update(editingId, values);
        message.success('Evento atualizado com sucesso');
      } else {
        await eventoService.create(values);
        message.success('Evento criado com sucesso');
      }
      setModalAberto(false);
      recarregar(page, pageSize);
    } catch (err) {
      handleApiError(err, 'Erro ao salvar evento');
    } finally {
      setSalvando(false);
    }
  };

  const handleCancelar = async (evento) => {
    try {
      await eventoService.cancelar(evento.id);
      message.success('Evento cancelado');
      recarregar(page, pageSize);
    } catch (err) {
      handleApiError(err, 'Erro ao cancelar evento');
    }
  };

  const handleExcluir = async (evento) => {
    try {
      await eventoService.delete(evento.id);
      message.success('Evento excluído');
      recarregar(page, pageSize);
    } catch (err) {
      handleApiError(err, 'Não foi possível excluir o evento');
    }
  };

  const nomeUnidade = (idUnidades) => unidades.find((u) => u.id === idUnidades)?.nome_fantasia || idUnidades;

  // Traduz o estado de ordenacao (server-side) para o `sortOrder` visual do
  // antd -- garante que o indicador do cabecalho reflita a ordenacao REAL
  // aplicada pelo backend, nunca uma ordenacao local.
  const ordemAntd = (key) =>
    sortBy === key ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null;

  // onChange unico da Table: paginacao + ordenacao server-side. Trocar a
  // ordenacao sempre volta para a pagina 1. NUNCA ordena localmente.
  const handleTableChange = (paginacao, _filtros, sorter) => {
    const novoSortBy = sorter && sorter.order ? sorter.columnKey || sorter.field : null;
    const novoSortOrder =
      sorter && sorter.order === 'ascend'
        ? 'asc'
        : sorter && sorter.order === 'descend'
        ? 'desc'
        : null;
    const sortMudou = novoSortBy !== sortBy || novoSortOrder !== sortOrder;
    const novaPagina = sortMudou ? 1 : paginacao.current || 1;
    const novoTamanho = paginacao.pageSize || pageSize;
    setSortBy(novoSortBy);
    setSortOrder(novoSortOrder);
    carregarEventos(
      novaPagina,
      novoTamanho,
      { status: filtroStatus, id_unidades: filtroUnidade },
      { sortBy: novoSortBy, sortOrder: novoSortOrder },
    );
  };

  const aplicarFiltroStatus = (valor) => {
    const proximo = valor || FILTRO_STATUS_PADRAO;
    setFiltroStatus(proximo);
    carregarEventos(1, pageSize, { status: proximo, id_unidades: filtroUnidade }, { sortBy, sortOrder });
  };

  const aplicarFiltroUnidade = (valor) => {
    setFiltroUnidade(valor);
    carregarEventos(1, pageSize, { status: filtroStatus, id_unidades: valor }, { sortBy, sortOrder });
  };

  const limparFiltros = () => {
    // Volta ao estado operacional padrao: STATUS = ATIVO, sem unidade,
    // ordenacao padrao (Evento.id DESC no backend).
    setFiltroStatus(FILTRO_STATUS_PADRAO);
    setFiltroUnidade(undefined);
    setSortBy(null);
    setSortOrder(null);
    carregarEventos(1, pageSize, { status: FILTRO_STATUS_PADRAO }, {});
  };

  const columns = [
    { title: 'Nome', dataIndex: 'nome', key: 'nome', sorter: true, sortOrder: ordemAntd('nome') },
    { title: 'Unidade', dataIndex: 'id_unidades', key: 'id_unidades', render: nomeUnidade },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortOrder: ordemAntd('status'),
      render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
    },
    {
      title: 'Criado em',
      dataIndex: 'criado_em',
      key: 'criado_em',
      sorter: true,
      sortOrder: ordemAntd('criado_em'),
      render: (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, evento) => (
        <Space>
          {podeEditar && evento.status === 'ATIVO' && (
            <Button size="small" onClick={() => abrirEditar(evento)}>
              Editar
            </Button>
          )}
          {podeEditar && evento.status === 'ATIVO' && (
            <Popconfirm
              title="Cancelar este evento?"
              description="Todas as sessões ainda agendadas também serão canceladas."
              onConfirm={() => handleCancelar(evento)}
              okText="Confirmar"
              cancelText="Cancelar"
            >
              <Button size="small" danger>
                Cancelar
              </Button>
            </Popconfirm>
          )}
          {podeExcluir && (
            <Popconfirm
              title="Excluir definitivamente este evento?"
              description="Exclua primeiro as sessões deste evento."
              onConfirm={() => handleExcluir(evento)}
              okText="Confirmar"
              cancelText="Cancelar"
            >
              <Button size="small" danger type="text">
                Excluir
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!permissaoLoading && !podeVisualizar && !isAdmin) {
    return (
      <Card>
        <Typography.Text type="secondary">Você não tem permissão para visualizar Eventos.</Typography.Text>
      </Card>
    );
  }

  return (
    <Card
      title="Eventos"
      extra={
        podeCriar && (
          <Button type="primary" onClick={abrirCriar}>
            Novo Evento
          </Button>
        )
      }
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={filtroStatus}
          onChange={aplicarFiltroStatus}
          options={OPCOES_FILTRO_STATUS}
          style={{ width: 160 }}
          aria-label="Filtrar por status"
        />
        <Select
          allowClear
          value={filtroUnidade}
          onChange={aplicarFiltroUnidade}
          placeholder="Todas as unidades"
          options={unidades.map((u) => ({ value: u.id, label: u.nome_fantasia }))}
          style={{ width: 220 }}
          aria-label="Filtrar por unidade"
        />
        <Button onClick={limparFiltros}>Limpar filtros</Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading || permissaoLoading}
        dataSource={eventos}
        columns={columns}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: (evento) => (
            <SessoesPainel
              evento={evento}
              podeCriar={podeCriar}
              podeEditar={podeEditar}
              podeExcluir={podeExcluir}
            />
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `Total: ${t}`,
        }}
      />

      <Modal
        title={editingId ? 'Editar Evento' : 'Novo Evento'}
        open={modalAberto}
        onCancel={() => setModalAberto(false)}
        onOk={() => form.submit()}
        confirmLoading={salvando}
        okText="Salvar"
        cancelText="Cancelar"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="id_unidades"
            label="Unidade"
            rules={[{ required: true, message: 'Selecione a unidade' }]}
          >
            <Select
              options={unidades.map((u) => ({ value: u.id, label: u.nome_fantasia }))}
              placeholder="Selecione a unidade"
            />
          </Form.Item>
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, whitespace: true, message: 'Informe o nome do evento' }]}
          >
            <Input maxLength={200} placeholder='Ex.: "Apresentações de Fim de Ano"' />
          </Form.Item>
          <Form.Item name="descricao" label="Descrição">
            <TextArea rows={3} placeholder="Descrição opcional" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default EventosAdmin;
